// Migration par lot des dossiers en cours vers une version publiée : choix des versions sources,
// motif obligatoire, rapport renvoyé par le serveur (migrés avec avertissements, refusés avec les
// états absents). Le rapport reste affiché jusqu'à la fermeture de la page.
import type { Demarche, RapportMigration } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Dialogue, Lien, Panneau, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { LIBELLES_ETAT_VERSION } from './contenu';
import styles from './demarches.module.css';
import commun from '../../commun/commun.module.css';

export function RapportMigrationVue({ rapport }: { rapport: RapportMigration }) {
  return (
    <div className={styles.rapport} data-testid="rapport-migration">
      <div>
        <h3>
          {rapport.migres.length} dossier{rapport.migres.length > 1 ? 's' : ''} migré{rapport.migres.length > 1 ? 's' : ''} vers la version {rapport.version_cible.numero}
        </h3>
        {rapport.migres.length === 0 ? <p className={commun.na}>Aucun dossier migré.</p> : null}
        <ul>
          {rapport.migres.map((m) => (
            <li key={m.dossier_id}>
              <Lien href={`/dossiers/${m.dossier_id}`}>{m.numero ?? 'sans numéro'}</Lien> (depuis la version {m.version_source})
              {m.avertissements.length > 0 ? <span className={commun.na}> : {m.avertissements.join(' ; ')}</span> : null}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3>
          {rapport.refuses.length} dossier{rapport.refuses.length > 1 ? 's' : ''} refusé{rapport.refuses.length > 1 ? 's' : ''}
        </h3>
        {rapport.refuses.length === 0 ? <p className={commun.na}>Aucun refus.</p> : null}
        <ul>
          {rapport.refuses.map((r) => (
            <li key={r.dossier_id}>
              <Lien href={`/dossiers/${r.dossier_id}`}>{r.numero ?? 'sans numéro'}</Lien> (version {r.version_source}) : {r.motif}
              {r.etats_absents.length > 0 ? <span className={commun.na}> ; états absents : {r.etats_absents.join(', ')}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function MigrationLot({ demarche }: { demarche: Demarche }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [rapport, setRapport] = useState<RapportMigration | null>(null);
  const cible = demarche.version_courante;
  const anterieures = demarche.versions.filter((v) => cible && v.numero < cible.numero && (v.etat === 'remplacee' || v.etat === 'publiee'));
  const migration = useAction({
    executer: () => requete<RapportMigration>(`/demarches/${demarche.id}/versions/${cible?.numero}/migrer-lot`, { methode: 'POST', corps: { motif, versions_source: sources.length > 0 ? sources : undefined } }),
    succes: (r) => `Migration terminée : ${r.migres.length} dossier(s) migré(s), ${r.refuses.length} refusé(s).`,
    invalider: [['dossiers'], ['demarches'], ['indicateurs']],
    apres: (r) => {
      setRapport(r);
      setOuvert(false);
      setMotif('');
    },
  });
  if (!cible) return null;
  return (
    <Panneau titre="Migration des dossiers en cours" actions={anterieures.length > 0 ? <Bouton variante="secondaire" onClick={() => setOuvert(true)}>Migrer vers la version {cible.numero}</Bouton> : null}>
      <p>
        Les dossiers déposés avec une version antérieure gardent les règles de leur version. Une migration explicite, motivée et journalisée, les rattache à la version {cible.numero} ; un dossier dont une étape déjà franchie n'existe plus dans la version cible est refusé.
      </p>
      {anterieures.length === 0 ? <p className={commun.na}>Aucune version antérieure : rien à migrer.</p> : null}
      {rapport ? <RapportMigrationVue rapport={rapport} /> : null}
      <Dialogue
        ouvert={ouvert}
        titre={`Migrer les dossiers en cours vers la version ${cible.numero}`}
        onFermer={() => setOuvert(false)}
        actions={
          <>
            <Bouton variante="secondaire" onClick={() => setOuvert(false)}>
              Annuler
            </Bouton>
            <Bouton chargement={migration.enCours} disabled={motif.trim().length < 3} onClick={() => migration.lancer(undefined)}>
              Lancer la migration
            </Bouton>
          </>
        }
      >
        <fieldset className={styles.sousEditeur}>
          <legend>Versions sources (toutes les versions antérieures si aucune n'est cochée)</legend>
          {anterieures.map((v) => (
            <CaseACocher key={v.id} libelle={`Version ${v.numero} (${LIBELLES_ETAT_VERSION[v.etat] ?? v.etat})`} checked={sources.includes(v.id)} onChange={(e) => setSources(e.target.checked ? [...sources, v.id] : sources.filter((x) => x !== v.id))} />
          ))}
        </fieldset>
        <ZoneTexte libelle="Motif de la migration" value={motif} required aide="Inscrit dans l'historique de chaque dossier migré et dans le journal des migrations." onChange={(e) => setMotif(e.target.value)} />
        {migration.messageErreur ? <Bandeau type="erreur">{migration.messageErreur}</Bandeau> : null}
      </Dialogue>
    </Panneau>
  );
}
